import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Save, 
  User, 
  Shield, 
  Bell, 
  Palette,

  Key,
  Eye,
  EyeOff,
  Download,
  Upload,
  Trash2,

} from 'lucide-react'
import Navigation from '../components/Navigation'

export default function DemoSettingsPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('profile')
  const [showPassword, setShowPassword] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)

  // Mock设置数据
  const [settings, setSettings] = useState({
    profile: {
      username: 'demo_user',
      email: 'demo@example.com',
      fullName: '演示用户',
      bio: '这是一个演示账户，用于展示TideLab的功能。',
      avatar: null
    },
    security: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      twoFactorEnabled: false,
      sessionTimeout: 30,
      loginNotifications: true
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      strategyAlerts: true,
      riskAlerts: true,
      marketUpdates: false,
      weeklyReports: true
    },
    appearance: {
      theme: 'auto',
      language: 'zh-CN',
      timezone: 'Asia/Shanghai',
      dateFormat: 'YYYY-MM-DD',
      timeFormat: '24h',
      compactMode: false
    },
    api: {
      binanceApiKey: 'demo_binance_key_123456',
      binanceSecretKey: 'demo_binance_secret_123456',
      okxApiKey: 'demo_okx_key_123456',
      okxSecretKey: 'demo_okx_secret_123456',
      bybitApiKey: 'demo_bybit_key_123456',
      bybitSecretKey: 'demo_bybit_secret_123456'
    }
  })

  const handleSettingChange = (section: string, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: value
      }
    }))
  }

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">个人信息</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              用户名
            </label>
            <input
              type="text"
              value={settings.profile.username}
              onChange={(e) => handleSettingChange('profile', 'username', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              邮箱地址
            </label>
            <input
              type="email"
              value={settings.profile.email}
              onChange={(e) => handleSettingChange('profile', 'email', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              真实姓名
            </label>
            <input
              type="text"
              value={settings.profile.fullName}
              onChange={(e) => handleSettingChange('profile', 'fullName', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              个人简介
            </label>
            <textarea
              value={settings.profile.bio}
              onChange={(e) => handleSettingChange('profile', 'bio', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">账户统计</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-white/5 rounded-xl">
            <div className="text-2xl font-bold text-gradient mb-2">15</div>
            <div className="text-sm text-ocean-secondary">创建策略</div>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-xl">
            <div className="text-2xl font-bold text-gradient mb-2">89</div>
            <div className="text-sm text-ocean-secondary">总交易次数</div>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-xl">
            <div className="text-2xl font-bold text-gradient mb-2">68.5%</div>
            <div className="text-sm text-ocean-secondary">平均胜率</div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">密码设置</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              当前密码
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={settings.security.currentPassword}
                onChange={(e) => handleSettingChange('security', 'currentPassword', e.target.value)}
                className="w-full px-4 py-3 pr-12 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ocean-secondary hover:text-ocean-primary"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              新密码
            </label>
            <input
              type="password"
              value={settings.security.newPassword}
              onChange={(e) => handleSettingChange('security', 'newPassword', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              确认新密码
            </label>
            <input
              type="password"
              value={settings.security.confirmPassword}
              onChange={(e) => handleSettingChange('security', 'confirmPassword', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">安全设置</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
            <div>
              <h4 className="font-medium text-ocean-primary">两步验证</h4>
              <p className="text-sm text-ocean-secondary">启用Google Authenticator或短信验证</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.security.twoFactorEnabled}
                onChange={(e) => handleSettingChange('security', 'twoFactorEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
            <div>
              <h4 className="font-medium text-ocean-primary">登录通知</h4>
              <p className="text-sm text-ocean-secondary">新设备登录时发送邮件通知</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.security.loginNotifications}
                onChange={(e) => handleSettingChange('security', 'loginNotifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              会话超时时间（分钟）
            </label>
            <select
              value={settings.security.sessionTimeout}
              onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value={15}>15分钟</option>
              <option value={30}>30分钟</option>
              <option value={60}>1小时</option>
              <option value={120}>2小时</option>
              <option value={0}>永不超时</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  )

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">通知设置</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
            <div>
              <h4 className="font-medium text-ocean-primary">邮件通知</h4>
              <p className="text-sm text-ocean-secondary">接收重要事件的邮件通知</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notifications.emailNotifications}
                onChange={(e) => handleSettingChange('notifications', 'emailNotifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
            <div>
              <h4 className="font-medium text-ocean-primary">推送通知</h4>
              <p className="text-sm text-ocean-secondary">接收浏览器推送通知</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notifications.pushNotifications}
                onChange={(e) => handleSettingChange('notifications', 'pushNotifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
            <div>
              <h4 className="font-medium text-ocean-primary">策略提醒</h4>
              <p className="text-sm text-ocean-secondary">策略执行和状态变化提醒</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notifications.strategyAlerts}
                onChange={(e) => handleSettingChange('notifications', 'strategyAlerts', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
            <div>
              <h4 className="font-medium text-ocean-primary">风险提醒</h4>
              <p className="text-sm text-ocean-secondary">风险指标异常和回撤提醒</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notifications.riskAlerts}
                onChange={(e) => handleSettingChange('notifications', 'riskAlerts', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
            <div>
              <h4 className="font-medium text-ocean-primary">市场更新</h4>
              <p className="text-sm text-ocean-secondary">市场动态和新闻推送</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notifications.marketUpdates}
                onChange={(e) => handleSettingChange('notifications', 'marketUpdates', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
            <div>
              <h4 className="font-medium text-ocean-primary">周报</h4>
              <p className="text-sm text-ocean-secondary">每周策略表现总结报告</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notifications.weeklyReports}
                onChange={(e) => handleSettingChange('notifications', 'weeklyReports', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  )

  const renderAppearanceTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">外观设置</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              主题模式
            </label>
            <select
              value={settings.appearance.theme}
              onChange={(e) => handleSettingChange('appearance', 'theme', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="auto">跟随系统</option>
              <option value="light">浅色模式</option>
              <option value="dark">深色模式</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              语言
            </label>
            <select
              value={settings.appearance.language}
              onChange={(e) => handleSettingChange('appearance', 'language', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="zh-CN">简体中文</option>
              <option value="en-US">English</option>
              <option value="ja-JP">日本語</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              时区
            </label>
            <select
              value={settings.appearance.timezone}
              onChange={(e) => handleSettingChange('appearance', 'timezone', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="Asia/Shanghai">中国标准时间 (UTC+8)</option>
              <option value="America/New_York">美国东部时间 (UTC-5)</option>
              <option value="Europe/London">格林威治时间 (UTC+0)</option>
              <option value="Asia/Tokyo">日本标准时间 (UTC+9)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              时间格式
            </label>
            <select
              value={settings.appearance.timeFormat}
              onChange={(e) => handleSettingChange('appearance', 'timeFormat', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="24h">24小时制</option>
              <option value="12h">12小时制</option>
            </select>
          </div>
        </div>
        
        <div className="mt-6">
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
            <div>
              <h4 className="font-medium text-ocean-primary">紧凑模式</h4>
              <p className="text-sm text-ocean-secondary">减少界面间距，显示更多内容</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.appearance.compactMode}
                onChange={(e) => handleSettingChange('appearance', 'compactMode', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  )

  const renderApiTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">API密钥管理</h3>
        
        <div className="space-y-6">
          {/* Binance API */}
          <div className="p-4 bg-white/5 rounded-xl">
            <h4 className="font-medium text-ocean-primary mb-4">Binance API</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ocean-secondary mb-2">
                  API Key
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={settings.api.binanceApiKey}
                    onChange={(e) => handleSettingChange('api', 'binanceApiKey', e.target.value)}
                    className="w-full px-4 py-3 pr-12 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                  <button
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ocean-secondary hover:text-ocean-primary"
                  >
                    {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-ocean-secondary mb-2">
                  Secret Key
                </label>
                <input
                  type="password"
                  value={settings.api.binanceSecretKey}
                  onChange={(e) => handleSettingChange('api', 'binanceSecretKey', e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>
          </div>

          {/* OKX API */}
          <div className="p-4 bg-white/5 rounded-xl">
            <h4 className="font-medium text-ocean-primary mb-4">OKX API</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ocean-secondary mb-2">
                  API Key
                </label>
                <input
                  type="password"
                  value={settings.api.okxApiKey}
                  onChange={(e) => handleSettingChange('api', 'okxApiKey', e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ocean-secondary mb-2">
                  Secret Key
                </label>
                <input
                  type="password"
                  value={settings.api.okxSecretKey}
                  onChange={(e) => handleSettingChange('api', 'okxSecretKey', e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>
          </div>

          {/* Bybit API */}
          <div className="p-4 bg-white/5 rounded-xl">
            <h4 className="font-medium text-ocean-primary mb-4">Bybit API</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ocean-secondary mb-2">
                  API Key
                </label>
                <input
                  type="password"
                  value={settings.api.bybitApiKey}
                  onChange={(e) => handleSettingChange('api', 'bybitApiKey', e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ocean-secondary mb-2">
                  Secret Key
                </label>
                <input
                  type="password"
                  value={settings.api.bybitSecretKey}
                  onChange={(e) => handleSettingChange('api', 'bybitSecretKey', e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">数据管理</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="btn-secondary flex items-center justify-center">
            <Download className="w-4 h-4 mr-2" />
            导出数据
          </button>
          <button className="btn-secondary flex items-center justify-center">
            <Upload className="w-4 h-4 mr-2" />
            导入数据
          </button>
          <button className="btn-secondary flex items-center justify-center text-red-400 hover:text-red-300">
            <Trash2 className="w-4 h-4 mr-2" />
            清除数据
          </button>
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileTab()
      case 'security':
        return renderSecurityTab()
      case 'notifications':
        return renderNotificationsTab()
      case 'appearance':
        return renderAppearanceTab()
      case 'api':
        return renderApiTab()
      default:
        return renderProfileTab()
    }
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面头部 */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/demo')}
                className="btn-secondary flex items-center justify-center"
                title="返回主页"
              >
                <ArrowLeft className="w-4 h-4 mr-2 sm:mr-2" />
                <span className="hidden sm:inline">返回主页</span>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gradient">设置</h1>
                <p className="text-ocean-secondary">管理您的账户设置和偏好</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button className="btn-primary flex items-center justify-center" title="保存设置">
              <Save className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">保存设置</span>
            </button>
          </div>
        </div>

        {/* 标签页导航 */}
        <div className="card mb-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('profile')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'profile'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              <User className="w-4 h-4 mr-2 inline" />
              个人信息
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'security'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              <Shield className="w-4 h-4 mr-2 inline" />
              安全设置
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'notifications'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              <Bell className="w-4 h-4 mr-2 inline" />
              通知设置
            </button>
            <button
              onClick={() => setActiveTab('appearance')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'appearance'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              <Palette className="w-4 h-4 mr-2 inline" />
              外观设置
            </button>
            <button
              onClick={() => setActiveTab('api')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'api'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              <Key className="w-4 h-4 mr-2 inline" />
              API管理
            </button>
          </div>
        </div>

        {/* 标签页内容 */}
        {renderTabContent()}
      </main>
    </div>
  )
} 